import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useToast } from "@/hooks/use-toast";
import Navbar from "@/components/Navbar";
import WebsiteCard from "@/components/WebsiteCard";
import WebsiteListView from "@/components/WebsiteListView";
import { PlusCircle, Plug, LayoutGrid, LayoutList } from "lucide-react";
import { 
  backupWebsite, 
  restoreWebsite, 
  setupSampleWebsites,
  getWebsiteById,
  getAllWebsitesFromDb,
  type Website
} from "@/services/wordpressService";
import { setupDemoPluginRegistration } from "@/services/wordpressPluginService";
import { useQuery } from "@tanstack/react-query";

export default function Dashboard() {
  const [websites, setWebsites] = useState<Website[]>([]);
  const [isLoading, setIsLoading] = useState<Record<string, boolean>>({});
  const [viewMode, setViewMode] = useState<"grid" | "list">("grid");
  const { toast } = useToast();

  // Query for fetching all websites
  const websitesQuery = useQuery({
    queryKey: ['websites'],
    queryFn: getAllWebsitesFromDb,
  });

  // Initialize database and load websites
  useEffect(() => {
    const initializeApp = async () => {
      // Setup sample websites with credentials
      await setupSampleWebsites();
      
      // Setup demo plugin registrations
      await setupDemoPluginRegistration();
      
      // Load websites from database
      const loadedWebsites = await getAllWebsitesFromDb();
      setWebsites(loadedWebsites);
    };

    initializeApp();
  }, []);

  // Update websites state when query data is available
  useEffect(() => {
    if (websitesQuery.data) {
      setWebsites(websitesQuery.data);
    }
  }, [websitesQuery.data]);

  // Handle backup now action with real WordPress API
  const handleBackupNow = async (websiteId: string) => {
    console.log(`Starting backup process for website ID: ${websiteId}`);
    
    // Set loading state for this website
    setIsLoading(prev => ({ ...prev, [websiteId]: true }));
    
    try {
      // Get the website directly from current state rather than searching again
      const website = websites.find(site => site.id === websiteId);
      
      if (!website) {
        console.error(`Website with ID ${websiteId} not found in current websites array:`, websites);
        
        // Try to get it directly from the database as a fallback
        const dbWebsite = await getWebsiteById(websiteId);
        
        if (!dbWebsite) {
          console.error(`Website with ID ${websiteId} not found in database either`);
          
          toast({
            title: "Backup failed",
            description: `Website with ID ${websiteId} not found.`,
            variant: "destructive",
          });
          
          return;
        }
        
        console.log("Using website from database:", dbWebsite);
        
        // Only proceed with WordPress websites
        if (dbWebsite.platform === "wordpress") {
          console.log(`Starting WordPress backup for ${dbWebsite.title}`);
          
          // Trigger actual WordPress backup
          const backupResult = await backupWebsite(dbWebsite);
          
          console.log("Backup result from backupWebsite:", backupResult);
          
          if (backupResult) {
            // Refresh websites data
            websitesQuery.refetch();
          }
        } else {
          // For non-WordPress sites, just show a toast for now
          toast({
            title: "Backup started",
            description: `Your ${dbWebsite.platform} backup is now in progress.`,
          });
        }
        
        return;
      }
      
      console.log(`Found website in state: ${website.title} (${website.platform})`);
      
      // Continue with the website from the state
      // Only proceed with WordPress websites
      if (website.platform === "wordpress") {
        console.log(`Starting WordPress backup for ${website.title}`);
        
        // Trigger actual WordPress backup
        const backupResult = await backupWebsite(website);
        
        console.log("Backup result from backupWebsite:", backupResult);
        
        if (backupResult) {
          // Update the website's last backup timestamp in local state
          const updatedWebsites = websites.map(site => 
            site.id === websiteId 
              ? { ...site, lastBackup: "Just now" } 
              : site
          );
          setWebsites(updatedWebsites);
          
          // Refresh websites data
          websitesQuery.refetch();
          
          console.log("Backup process completed successfully");
        } else {
          console.log("Backup process returned null - check previous error logs");
        }
      } else {
        // For non-WordPress sites, just show a toast for now
        toast({
          title: "Backup started",
          description: `Your ${website.platform} backup is now in progress.`,
        });
      }
    } catch (error) {
      console.error("Backup error in handleBackupNow:", error);
      
      // More detailed error logging
      if (error instanceof Error) {
        console.error("Error message:", error.message);
        console.error("Error stack:", error.stack);
      }
      
      toast({
        title: "Backup failed",
        description: "There was an error starting your backup. Check the console for details.",
        variant: "destructive",
      });
    } finally {
      // Clear loading state
      setIsLoading(prev => ({ ...prev, [websiteId]: false }));
      console.log(`Backup process finished for website ID: ${websiteId}`);
    }
  };

  // Handle view backups action
  const handleViewBackups = (websiteId: string) => {
    // No need to do anything here since we're using Link in WebsiteCard
    // The navigation is handled directly in the component
    console.log(`View backups for website ${websiteId}`);
  };

  // Handle restore action
  const handleRestore = (websiteId: string) => {
    // No need to do anything here since we're using Link in WebsiteCard
    // The navigation is handled directly in the component
    console.log(`Navigate to restore page for website ${websiteId}`);
  };

  // Show loading state while fetching websites
  if (websitesQuery.isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-xl font-semibold mb-2">Loading websites...</h2>
          <p className="text-gray-500">Connecting to MariaDB database</p>
        </div>
      </div>
    );
  }

  // Show error if website fetching failed
  if (websitesQuery.isError) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center max-w-md p-6 bg-white rounded-lg shadow-lg">
          <h2 className="text-xl font-semibold mb-2 text-red-600">Database Error</h2>
          <p className="text-gray-700 mb-4">
            There was an error connecting to the MariaDB database. Please make sure the database server is running and properly configured.
          </p>
          <Button onClick={() => websitesQuery.refetch()}>Try Again</Button>
        </div>
      </div>
    );
  }

  // Function to render websites based on view mode and platform filter
  const renderWebsites = (platformFilter?: string) => {
    const filteredWebsites = platformFilter 
      ? websites.filter(website => website.platform === platformFilter)
      : websites;

    if (viewMode === "grid") {
      return (
        <div className="grid gap-6 md:grid-cols-2">
          {filteredWebsites.map((website) => (
            <WebsiteCard
              key={website.id}
              websiteId={website.id}
              title={website.title}
              url={website.url}
              platform={website.platform}
              lastBackup={website.lastBackup}
              nextBackup={website.nextBackup}
              storageUsed={website.storageUsed}
              status={website.status}
              isLoading={!!isLoading[website.id]}
              onBackupNow={() => handleBackupNow(website.id)}
              onViewBackups={() => handleViewBackups(website.id)}
              onRestore={() => handleRestore(website.id)}
            />
          ))}
        </div>
      );
    } else {
      return (
        <WebsiteListView 
          websites={filteredWebsites}
          isLoading={isLoading}
          onBackupNow={handleBackupNow}
          onViewBackups={handleViewBackups}
          onRestore={handleRestore}
        />
      );
    }
  };

  // Main dashboard content
  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar isLoggedIn={true} />
      
      <div className="container mx-auto py-10 px-4 max-w-6xl">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-bold">Dashboard</h1>
          <div className="flex gap-2">
            <Button asChild variant="outline">
              <Link to="/wordpress-plugin">
                <Plug className="mr-2 h-4 w-4" />
                WordPress Plugin
              </Link>
            </Button>
            <Button asChild>
              <Link to="/add-website">
                <PlusCircle className="mr-2 h-4 w-4" />
                Add Website
              </Link>
            </Button>
          </div>
        </div>

        <div className="grid gap-6 md:grid-cols-3 mb-8">
          {/* Total Backups Card */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-gray-500">
                Total Backups
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold">24</div>
              <p className="text-xs text-gray-500 mt-1">+3 from last week</p>
            </CardContent>
          </Card>

          {/* Storage Used Card */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-gray-500">
                Storage Used
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold">2.4 GB</div>
              <p className="text-xs text-gray-500 mt-1">of 10 GB</p>
            </CardContent>
          </Card>

          {/* Recent Activity Card */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-gray-500">
                Recent Activity
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold">5 hours ago</div>
              <p className="text-xs text-gray-500 mt-1">Last backup completed</p>
            </CardContent>
          </Card>
        </div>

        <Tabs defaultValue="all" className="space-y-4">
          <div className="flex justify-between items-center">
            <TabsList>
              <TabsTrigger value="all">All Websites</TabsTrigger>
              <TabsTrigger value="wordpress">WordPress</TabsTrigger>
              <TabsTrigger value="shopify">Shopify</TabsTrigger>
              <TabsTrigger value="woocommerce">WooCommerce</TabsTrigger>
            </TabsList>
            
            <div className="flex gap-2 border rounded-md p-1 bg-muted">
              <Button 
                variant={viewMode === "grid" ? "default" : "ghost"} 
                size="sm"
                onClick={() => setViewMode("grid")}
                className="px-2"
              >
                <LayoutGrid className="h-4 w-4" />
              </Button>
              <Button 
                variant={viewMode === "list" ? "default" : "ghost"} 
                size="sm"
                onClick={() => setViewMode("list")}
                className="px-2"
              >
                <LayoutList className="h-4 w-4" />
              </Button>
            </div>
          </div>
          
          <TabsContent value="all" className="space-y-4">
            {renderWebsites()}
            
            {websites.length === 0 && (
              <div className="text-center py-12">
                <h3 className="text-lg font-medium text-gray-900 mb-2">No websites added yet</h3>
                <p className="text-gray-500 mb-4">Add your first website to start backing up your data.</p>
                <Button asChild>
                  <Link to="/add-website">
                    <PlusCircle className="mr-2 h-4 w-4" />
                    Add Website
                  </Link>
                </Button>
              </div>
            )}
          </TabsContent>
          
          <TabsContent value="wordpress">
            {renderWebsites("wordpress")}
          </TabsContent>
          
          <TabsContent value="shopify">
            {renderWebsites("shopify")}
          </TabsContent>
          
          <TabsContent value="woocommerce">
            {renderWebsites("woocommerce")}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
